import { useEffect, useRef, useState } from 'react'
import './app-shell.styles.scss'
import { NavLink, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faChevronLeft, faHouse, faXmark } from '@fortawesome/free-solid-svg-icons';
import UserIcon from '@/components/layout-auth/user-icon/user-icon.component';
import UserDropdown from '@/components/layout-auth/head-bar/user-dropdown.component';
import BrandLogo from '@/components/SHARED/brand-logo/brand-logo.component';
import { ShellThemeContext } from '@/utils/theme';

// Page frame shared by the admin and manager sides: a head bar and a side
// menu that becomes a slide-in drawer below 900px.
// title: shown beside the logo. items: [{to, label, icon, end?}].
// headLinks: [{to, label}] shown in the head bar on desktop.
// menuExtra(close): extra menu entries rendered below the items.
// theme: hardwood | varsity | arena (see themes.scss). A page inside can
// switch it with useShellTheme (the admin league pages do).
// tabs: up to three [{to, label, icon, end?}] for the bottom bar on phones,
// between Back and Menu (an installed app on iPhone has no back button).
// Without tabs the bar shows Home.
// themeVars: CSS variables that override the theme's (a college team's
// colors, utils/teamTheme.js).
const AppShell = ({ title, items, headLinks = [], menuExtra, theme = 'hardwood', themeVars, tabs, children }) => {

    const user = useSelector(state => state.userData.currentUser);
    const profile = useSelector(state => state.userData.userProfile);
    const [ menuOpen, setMenuOpen ] = useState(false);
    const [ showDropdown, setShowDropdown ] = useState(false);
    const [ pageTheme, setPageTheme ] = useState(null);
    const userMenuRef = useRef(null);
    const navigate = useNavigate();
    const close = () => setMenuOpen(false);
    const activeTheme = pageTheme ?? theme;
    const barTabs = (tabs?.length ? tabs : [{ to: '/home', label: 'Home', icon: faHouse }]).slice(0, 3);

    // Back within the app; with nowhere to go back to (the app was just
    // opened), to the first tab.
    function back() {
        close();
        if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
        else navigate(barTabs[0].to);
    }

    // Tapping anywhere outside the account menu closes it.
    useEffect(() => {
        if (!showDropdown) return;
        const onDown = (e) => {
            if (!userMenuRef.current?.contains(e.target)) setShowDropdown(false);
        };
        document.addEventListener('pointerdown', onDown);
        return () => document.removeEventListener('pointerdown', onDown);
    }, [showDropdown])

    return (
        <ShellThemeContext.Provider value={setPageTheme}>
        <div className={`app-shell theme-${activeTheme} ${title ? 'has-title' : ''}`} style={!pageTheme && themeVars ? themeVars : undefined}>
            <header className='app-head-bar'>
                <div className='head-left'>
                    <button
                        className='menu-toggle'
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={menuOpen}>
                        <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} size='lg' />
                    </button>
                    <div className='logo-container' onClick={() => navigate('/home')}>
                        <BrandLogo theme={activeTheme} />
                        {title && <>
                            <div className='title-spacer'></div>
                            <h3 className='app-title'>{title}</h3>
                        </>}
                    </div>
                </div>
                <div className='head-right'>
                    <nav className='head-links'>
                        {headLinks.map(link => (
                            <NavLink key={link.to} to={link.to}>{link.label}</NavLink>
                        ))}
                    </nav>
                    <div className='user-menu' ref={userMenuRef}>
                        {(user && profile) &&
                            <button
                                className='user-menu-button'
                                onClick={() => setShowDropdown(!showDropdown)}
                                aria-label='Account menu'
                                aria-expanded={showDropdown}>
                                <UserIcon profile={profile} reverse={true} white={true} />
                            </button>
                        }
                        {/* Choosing an item closes the menu. */}
                        <div onClick={() => setShowDropdown(false)}>
                            <UserDropdown show={showDropdown} />
                        </div>
                    </div>
                </div>
            </header>
            <div className='app-layout'>
                <div className={`app-menu-backdrop ${menuOpen ? 'open' : ''}`} onClick={close}></div>
                <nav className={`app-menu ${menuOpen ? 'open' : ''}`} aria-label='Main'>
                    {items.map(item => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            className='app-menu-item'
                            onClick={close}>
                            <FontAwesomeIcon className='app-menu-icon' icon={item.icon} size='lg' />
                            {item.label}
                        </NavLink>
                    ))}
                    {menuExtra && menuExtra(close)}
                </nav>
                <main className='app-content'>
                    {children}
                </main>
            </div>
            <nav className='app-tab-bar' aria-label='Quick links'>
                <button className='tab-item' onClick={back} aria-label='Back'>
                    <FontAwesomeIcon icon={faChevronLeft} />
                    <span>Back</span>
                </button>
                {barTabs.map(tab => (
                    <NavLink key={tab.to} to={tab.to} end={tab.end} className='tab-item' onClick={close}>
                        <FontAwesomeIcon icon={tab.icon} />
                        <span>{tab.label}</span>
                    </NavLink>
                ))}
                <button className={`tab-item ${menuOpen ? 'active' : ''}`} onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen} aria-label={menuOpen ? 'Close menu' : 'Open menu'}>
                    <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} />
                    <span>Menu</span>
                </button>
            </nav>
        </div>
        </ShellThemeContext.Provider>
    )
}

export default AppShell
